import React, { useState, useEffect } from 'react';
import RenderProfilePic from '../../../../extras/RenderProfilePic';
import {
  getStudents,
  createNewJobGroup,
  updateJobDescriptionDoc,
} from '../../../../services/apiService';
import { getFullName } from '../../../../utils';
import { useHistory } from 'react-router-dom';
import { Student } from '../../_components/Student';
import LoadingOverlay from '../../../../extras/LoadingOverlay';

export const AddStudents = ({
  basicDetails,
  recruiterDetails,
  jobDescriptionDocument,
}) => {
  const history = useHistory();
  const [students, setStudents] = useState([]);
  const [filters, setFilters] = useState({
    courses: [],
  });
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredStudents, setFilteredStudents] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchStudents = async () => {
    try {
      setLoading(true);
      const response = await getStudents();
      const _students = response?.data.map((student) => ({
        ...student,
        selected: false,
      }));
      setLoading(false);
      setStudents(_students);
    } catch (error) {
      setLoading(false);
      console.log(error);
    }
  };

  const updateJDDoc = async (groupId) => {
    try {
      const formData = new FormData();
      formData.append('groupId', groupId);
      formData.append('file', jobDescriptionDocument);
      await updateJobDescriptionDoc(formData);
    } catch (error) {
      console.log(error);
    }
  };

  // create new job group
  const createJobGroup = async () => {
    try {
      setLoading(true);
      const response = await createNewJobGroup({
        companyId: basicDetails.companyId,
        recruiterId: recruiterDetails.recruiterId,
        jobTitleId: basicDetails.jobTitleId,
        students: selectedStudents,
        jobDescription: basicDetails.description,
      });
      updateJDDoc(response.data.groupId);
      setLoading(false);
      history.push(`/job-dashboardv2/job-groups`);
    } catch (error) {
      setLoading(false);
      console.log(error);
    }
  };

  // filter students based on search query
  const filterStudents = () => {
    let _filteredStudents = students.filter((student) => {
      const { userDetails, courseType } = student;
      const name = getFullName(userDetails);
      // check if coursetype is in the filters.course array
      const courseTypeMatch =
        filters.courses.length === 0 || filters.courses.includes(courseType);

      return (
        courseTypeMatch &&
        name.toLowerCase().includes(searchQuery.toLowerCase())
      );
    });

    _filteredStudents = _filteredStudents.map((student) => {
      // if student found in selectedStudents, set selected to true
      const _student = selectedStudents.find(
        (selectedStudent) => selectedStudent === student.userId
      );
      if (_student) {
        return {
          ...student,
          selected: true,
        };
      }
      return student;
    });
    setFilteredStudents(_filteredStudents);
  };

  const addStudent = (studentId) => {
    // check if student is already selected
    const isStudentSelected = selectedStudents.includes(studentId);
    if (!isStudentSelected) {
      setSelectedStudents([...selectedStudents, studentId]);
      // update the selected state of the student in filteredStudents
      const _filteredStudents = filteredStudents.map((student) => {
        if (student.userId === studentId) {
          return { ...student, selected: true };
        }
        return student;
      });
      setFilteredStudents(_filteredStudents);
    }
  };

  const removeStudent = (studentId) => {
    // check if student is already selected
    const isStudentSelected = selectedStudents.includes(studentId);
    if (isStudentSelected) {
      setSelectedStudents(selectedStudents.filter((id) => id !== studentId));
      // update the selected state of the student in filteredStudents
      const _filteredStudents = filteredStudents.map((student) => {
        if (student.userId === studentId) {
          return { ...student, selected: false };
        }
        return student;
      });
      setFilteredStudents(_filteredStudents);
    }
  };

  // add or remove students from selected students array
  const handleSelect = (isSelected, studentId) => {
    if (isSelected) {
      addStudent(studentId);
    } else {
      removeStudent(studentId);
    }
  };

  // fetch job groups on component mount
  useEffect(() => {
    fetchStudents();
  }, []);

  // filter students on search query / filters or students change
  useEffect(() => {
    filterStudents();
  }, [searchQuery, students, filters.courses]);

  return (
    <div className='grid grid-cols-12 gap-4'>
      <div className='col-span-9 '>
        {loading && <LoadingOverlay />}
        {/* Add student header start */}
        <div className='flex items-center justify-between px-5 py-3 bg-white rounded shadow-md'>
          <div className='flex'>
            <RenderProfilePic
              thumbnail={basicDetails?.companyAvatar}
              name={basicDetails?.companyName}
              size={3}
            />
            <div className='flex-col ml-4'>
              <div className='font-bold text-font-black'>
                {basicDetails?.jobTitle}
              </div>
              <div className='text-font-dark'>{basicDetails?.companyName}</div>
            </div>
          </div>

          <div className='flex-col ml-4 text-xs text-right'>
            <div className='font-semibold text-font-dark'>
              Recruiter details:
            </div>
            <div> {recruiterDetails?.name}</div>
            <div> {recruiterDetails?.email}</div>
            <div> {recruiterDetails?.phone}</div>
          </div>
        </div>
        {/* Add student header end */}
        <div className='flex items-center justify-between pt-8'>
          <form
            className='flex items-center'
            onSubmit={(e) => {
              e.preventDefault();
            }}
          >
            <label htmlFor='simple-search' className='sr-only'>
              Search by name
            </label>
            <div className='relative w-full'>
              <div className='absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none'>
                <svg
                  aria-hidden='true'
                  className='w-5 h-5 text-gray-500 dark:text-gray-400'
                  fill='currentColor'
                  viewBox='0 0 20 20'
                  xmlns='http://www.w3.org/2000/svg'
                >
                  <path
                    fillRule='evenodd'
                    d='M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z'
                    clipRule='evenodd'
                  />
                </svg>
              </div>
              <input
                type='text'
                id='simple-search'
                className='bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full pl-10 p-2.5  dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500'
                placeholder='Search by name'
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          </form>

          <div>{`${selectedStudents.length} Selected`}</div>
        </div>
        <div className='flex-col mt-2 overflow-y-auto h-screen/2/5 '>
          {filteredStudents.map((student, i) => (
            <Student key={i} student={student} handleSelect={handleSelect} />
          ))}
        </div>
        <div
          style={{ width: '45%' }}
          className='flex flex-wrap justify-center mt-8 mb-2 -mx-3 fixed z-90 bottom-10 inset-x-1/4  w-96 '
        >
          <button
            onClick={() => {
              history.push(`/job-dashboardv2/job-groups`);
            }}
            className='px-8 py-2 ml-8 text-base font-medium border rounded  text-gray border-primary-400 bg-white hover:bg-gray-50'
          >
            Cancel
          </button>
          <button
            onClick={() => {
              createJobGroup();
            }}
            disabled={selectedStudents.length === 0 || loading}
            className={`${
              selectedStudents.length === 0 || loading
                ? 'bg-red-500'
                : 'bg-brand-primary '
            } text-base font-medium text-white border border-primary-400 px-8 py-2 ml-8 rounded`}
          >
            {/* fixed z-90 bottom-10 right-8 bg-blue-600 w-20 h-20 rounded-full drop-shadow-lg flex justify-center items-center text-white text-4xl hover:bg-blue-700 hover:drop-shadow-2xl hover:animate-bounce duration-300 */}
            Create Job Group
          </button>
        </div>
      </div>

      <div className='col-span-3 px-5'>
        {/* filter start */}
        <div className='flex-col items-center justify-between'>
          <div className='px-3 py-2 text-sm font-medium uppercase bg-red-c2'>
            {' '}
            Filter{' '}
          </div>
          <div className='px-3 py-4 bg-white rounded'>
            <h3 className='mb-3 text-xs font-bold text-font-black'>Courses</h3>
            <div className='flex mb-2'>
              <input
                type='checkbox'
                id='data-science'
                onChange={(event) => {
                  setFilters({
                    ...filters,
                    courses: event.target.checked
                      ? [...filters.courses, 'data-science']
                      : filters.courses.filter(
                          (course) => course !== 'data-science'
                        ),
                  });
                }}
              />
              <label className='ml-3 text-xs text-font-para'>
                Data Science
              </label>
            </div>
            <div className='flex'>
              <input
                type='checkbox'
                id='web3'
                onChange={(event) => {
                  setFilters({
                    ...filters,
                    courses: event.target.checked
                      ? [...filters.courses, 'web3']
                      : filters.courses.filter((course) => course !== 'web3'),
                  });
                }}
              />
              <label className='ml-3 text-xs text-font-para'>
                Web Development
              </label>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
